import {ActiveSelection, FabricImage, Group} from 'fabric';
import isValidURL from '../../lib/isValidURL.ts';
import {ActionGlyphs, LinkGlyph} from './resourceCardIcons.ts';

const BADGE_SHARE = 0.2;
const BADGE_MIN = 16;
const BADGE_MAX = 28;
const GLYPH_SHARE = 0.55;

let openGlyphPaths: Path2D[] | null = null;

// Lucide's open-in-new-tab icon is all paths, so Path2D draws it as is.
const glyphPaths = (): Path2D[] => {
    if (!openGlyphPaths) {
        openGlyphPaths = ActionGlyphs.open
            .filter(([tag]) => tag === 'path')
            .map(([, attrs]) => new Path2D(String((attrs as {d: string}).d)));
    }
    return openGlyphPaths;
};

/*
 * A whiteboard picture that can optionally be a web link. Every image the
 * board adds or restores is one of these; with no link it behaves and
 * serializes exactly like a plain FabricImage (it inherits the 'image' type).
 *
 * A linked image opens like a link resource card: a single click in
 * read-only mode, a double-click or the toolbar while editing. The link is
 * kept apart from nav on purpose, because nav holds in-app routes that go to
 * react-router's navigate.
 *
 * The badge in the bottom-right corner is drawn after the image itself,
 * outside its cache and clip path, so it is never cropped and never makes the
 * cache stale. It is sized in board units so it zooms with everything else.
 */
export class LinkableImage extends FabricImage {
    /*
     * undefined: never linked, and left out of the serialized element.
     * null: a link that was removed, serialized so the removal is persisted
     * (element changes are diffed only on the keys that are present).
     *
     * Declared rather than initialized: fromObject (and so clone) passes the
     * link through the constructor's options, and a field initializer would
     * run afterwards and wipe it.
     */
    declare link: string | null | undefined;

    /*
     * A linked image inside a group keeps its link but cannot be clicked,
     * since clicks land on the group, so it also stops showing the badge.
     */
    get isLinkActive(): boolean {
        return (
            typeof this.link === 'string' &&
            isValidURL(this.link) &&
            (!(this.group instanceof Group) ||
                this.group instanceof ActiveSelection)
        );
    }

    render(ctx: CanvasRenderingContext2D) {
        super.render(ctx);

        if (!this.isLinkActive || this.isNotVisible()) {
            return;
        }
        if (
            this.canvas &&
            this.canvas.skipOffscreen &&
            !this.group &&
            !this.isOnScreen()
        ) {
            return;
        }

        this.drawLinkBadge(ctx);
    }

    private drawLinkBadge(ctx: CanvasRenderingContext2D) {
        const scaleX = this.scaleX || 1;
        const scaleY = this.scaleY || 1;
        const displayWidth = this.width * Math.abs(scaleX);
        const displayHeight = this.height * Math.abs(scaleY);

        const size = Math.min(
            BADGE_MAX,
            Math.max(
                BADGE_MIN,
                Math.min(displayWidth, displayHeight) * BADGE_SHARE
            )
        );
        const radius = size / 2;
        const inset = size * 0.3;
        const centerX = displayWidth / 2 - inset - radius;
        const centerY = displayHeight / 2 - inset - radius;

        ctx.save();
        this.transform(ctx);
        // Back to unscaled, unflipped board units, so bottom-right stays put.
        ctx.scale(
            (this.flipX ? -1 : 1) / scaleX,
            (this.flipY ? -1 : 1) / scaleY
        );
        // The badge rides the rotated corner but its icon stays upright.
        ctx.translate(centerX, centerY);
        ctx.rotate((-this.getTotalAngle() * Math.PI) / 180);
        ctx.globalAlpha *= this.opacity;

        ctx.beginPath();
        ctx.arc(0, 0, radius, 0, Math.PI * 2);
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 6;
        ctx.shadowOffsetY = 2;
        ctx.fillStyle = LinkGlyph.accent;
        ctx.fill();
        ctx.shadowColor = 'transparent';
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = '#FFFFFF';
        ctx.stroke();

        // The glyph is drawn in Lucide's own 24x24 space.
        const glyphScale = (size * GLYPH_SHARE) / 24;
        ctx.translate(-12 * glyphScale, -12 * glyphScale);
        ctx.scale(glyphScale, glyphScale);
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        glyphPaths().forEach(path => ctx.stroke(path));

        ctx.restore();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    toObject(propertiesToInclude?: any): any {
        const object = super.toObject(propertiesToInclude);
        return this.link === undefined ? object : {...object, link: this.link};
    }
}
